const createCoverSection = (id: string, title: string, coverTitle: string, primaryColor: string) => ({
  id,
  title,
  content: {
    coverTitle,
    publishedDate: 'Published Date: {{publishedDate}}',
    brandGuideline: '{{brandName}}/Brand Guideline',
    designCredit: 'Designed By {{designCredit}}',
    brandLogo: ''
  },
  styles: {
    backgroundColor: primaryColor,
    textColor: '#FFFFFF',
    fontFamily: 'Helvetica',
    fontSize: '48px',
    padding: '60px',
    position: 'relative'
  },
  customization: {
    backgroundType: 'color',
    backgroundColor: primaryColor,
    backgroundImage: '',
    backgroundPosition: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    overlayEnabled: false,
    overlayColor: '#000000',
    overlayOpacity: 0,
    overlayBlendMode: 'normal'
  }
});

export const runFullBrandMigration = (brand: any) => {
  const existingSections = brand.sections || [];
  const primaryColor = brand.colors?.primary || '#3B82F6';

  let newSections = [...existingSections];

  // Remove old sections that are no longer needed
  const sectionsToRemove = ['typography', 'colors', 'brand-action', 'moodboard', 'thank-you'];
  const filteredSections = newSections.filter((s: any) => !sectionsToRemove.includes(s.id));

  if (filteredSections.length !== newSections.length) {
    newSections = filteredSections;
  }

  // Update existing cover section titles and table of contents
  newSections = newSections.map((s: any) => {
    if (s.id === 'brand-logo-cover' && s.content.coverTitle !== 'LOGO') {
      return { ...s, content: { ...s.content, coverTitle: 'LOGO' } };
    }
    if (s.id === 'color-cover' && s.content.coverTitle !== 'COLOR') {
      return { ...s, content: { ...s.content, coverTitle: 'COLOR' } };
    }
    // Update target-audience section to remove fontSize from audience styles
    if (s.id === 'target-audience') {
      const updatedContent = { ...s.content };
      let hasAudienceStyleChanges = false;

      // Remove fontSize from all audience styles
      Object.keys(updatedContent).forEach((key) => {
        if (key.startsWith('audience') && key.endsWith('Styles') && updatedContent[key]?.fontSize) {
          delete updatedContent[key].fontSize;
          hasAudienceStyleChanges = true;
        }
      });

      if (hasAudienceStyleChanges) {
        return { ...s, content: updatedContent };
      }
    }
    // Update guidelines section to use {{brandName}} shortcode and ensure white text colors
    if (s.id === 'guidelines') {
      const currentTitle = s.content?.brandBookTitle || '';
      const needsTitleUpdate = currentTitle.toUpperCase() === 'BRANDBOOK' || currentTitle === 'BrandBook';
      const needsColorUpdate =
        s.content?.brandBookTitleStyles?.color !== '#FFFFFF' ||
        s.content?.brandGuidelineStyles?.color !== '#FFFFFF' ||
        s.content?.designCreditStyles?.color !== '#FFFFFF';

      if (needsTitleUpdate || needsColorUpdate) {
        return {
          ...s,
          content: {
            ...s.content,
            brandBookTitle: needsTitleUpdate ? '{{brandName}}' : currentTitle,
            brandBookTitleStyles: {
              ...(s.content?.brandBookTitleStyles || {}),
              color: '#FFFFFF'
            },
            brandGuidelineStyles: {
              ...(s.content?.brandGuidelineStyles || {}),
              color: '#FFFFFF'
            },
            designCreditStyles: {
              ...(s.content?.designCreditStyles || {}),
              color: '#FFFFFF'
            }
          }
        };
      }
    }
    // Update table of contents to new structure
    if (s.id === 'table-of-contents') {
      const needsUpdate = !s.content.item1Title || s.content.item1Title !== 'Introduction';
      if (needsUpdate) {
        return {
          ...s,
          content: {
            ...s.content,
            tocImage: s.content.tocImage || '/img-placeholder-16-9.png',
            listTitle: s.content.listTitle || 'CONTENTS',
            item1Title: 'Introduction',
            item1Number: '01',
            item2Title: 'Logo',
            item2Number: '02',
            item3Title: 'Color',
            item3Number: '03',
            item4Title: 'Typography',
            item4Number: '04',
            item5Title: 'Moodboard',
            item5Number: '05',
            item6Title: 'Thank You',
            item6Number: '06'
          }
        };
      }
    }
    return s;
  });

  // Add missing cover sections
  const hasBrandLogoCover = newSections.some((s: any) => s.id === 'brand-logo-cover');
  const hasColorCover = newSections.some((s: any) => s.id === 'color-cover');
  const hasTypographyCover = newSections.some((s: any) => s.id === 'typography-cover');
  const hasTypographyFonts = newSections.some((s: any) => s.id === 'typography-fonts');
  const hasPhotographyCover = newSections.some((s: any) => s.id === 'photography-cover');
  const hasThankYouCover = newSections.some((s: any) => s.id === 'thank-you-cover');

  // Add Brand Logo Cover before brand-logo
  if (!hasBrandLogoCover) {
    const brandLogoIndex = newSections.findIndex((s: any) => s.id === 'brand-logo');
    if (brandLogoIndex !== -1) {
      const brandLogoCover = createCoverSection('brand-logo-cover', 'Brand Logo Cover', 'LOGO', primaryColor);
      newSections.splice(brandLogoIndex, 0, brandLogoCover);
    }
  }

  // Add Color Cover after do-dont
  if (!hasColorCover) {
    const doDontIndex = newSections.findIndex((s: any) => s.id === 'do-dont');
    if (doDontIndex !== -1) {
      const colorCover = createCoverSection('color-cover', 'Color Cover', 'COLOR', primaryColor);
      newSections.splice(doDontIndex + 1, 0, colorCover);
    }
  }

  // Add Typography Cover after color-cover
  if (!hasTypographyCover) {
    const colorCoverIndex = newSections.findIndex((s: any) => s.id === 'color-cover');
    if (colorCoverIndex !== -1) {
      const typographyCover = createCoverSection('typography-cover', 'Typography Cover', 'TYPOGRAPHY', primaryColor);
      newSections.splice(colorCoverIndex + 1, 0, typographyCover);
    }
  }

  // Add Typography Fonts after typography-cover
  if (!hasTypographyFonts) {
    const typographyCoverIndex = newSections.findIndex((s: any) => s.id === 'typography-cover');
    if (typographyCoverIndex !== -1) {
      const typographyFonts = {
        id: 'typography-fonts',
        title: 'Typography Fonts',
        content: {
          headingTitle: 'Heading Font',
          headingDescription: 'Use the heading font for page titles, section headers, and important headlines. It establishes visual hierarchy and captures attention with its distinctive character.',
          headingWeights: 'Font Weights: 400, 500, 700.',
          bodyTitle: 'Body Font',
          bodyDescription: 'Apply the body font for paragraphs, descriptions, captions, and longer text blocks. It ensures optimal readability and maintains consistency across all content.',
          bodyWeights: 'Font Weights: 400, 500, 700.'
        },
        styles: {
          backgroundColor: '#FFFFFF',
          textColor: '#000000',
          fontFamily: 'Helvetica',
          fontSize: '16px',
          padding: '60px',
          position: 'relative'
        },
        customization: {
          backgroundType: 'color',
          backgroundColor: '#FFFFFF',
          backgroundImage: '',
          backgroundPosition: 'center',
          backgroundSize: 'cover',
          backgroundRepeat: 'no-repeat',
          overlayEnabled: false,
          overlayColor: '#000000',
          overlayOpacity: 0,
          overlayBlendMode: 'normal'
        }
      };
      newSections.splice(typographyCoverIndex + 1, 0, typographyFonts);
    }
  }

  // Add Photography Cover after typography-fonts
  if (!hasPhotographyCover) {
    const typographyFontsIndex = newSections.findIndex((s: any) => s.id === 'typography-fonts');
    if (typographyFontsIndex !== -1) {
      const photographyCover = createCoverSection('photography-cover', 'Photography Cover', 'PHOTOGRAPHY', primaryColor);
      newSections.splice(typographyFontsIndex + 1, 0, photographyCover);
    }
  }

  // Add Thank You Cover at the end
  if (!hasThankYouCover) {
    const thankYouCover = createCoverSection('thank-you-cover', 'Thank You Cover', 'THANK YOU', primaryColor);
    newSections.push(thankYouCover);
  }

  return newSections;
};
